import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MatPaginator } from "@angular/material/paginator";
import { MatSort } from "@angular/material/sort";
import { MatTableDataSource } from "@angular/material/table";
import { faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import LocalizationConstants from "src/app/constants/localization-constants";
import { ReminderType } from "src/app/models/Enums";
import { Reminder } from "src/app/models/Reminder";
import { GlobalOverlayService } from "src/app/services/global-overlay.service";
import { ModalService } from "src/app/services/modal.service";
import { ReminderService } from "src/app/services/reminder.service";

import { ConfirmationDialogData } from "../../common/confirmation-dialog/confirmation-dialog-modal.component";
import { ReminderModalComponent } from "../../reminder-modal/reminder-modal.component";

@Component({
  selector: "app-country-reminders",
  templateUrl: "./country-reminders.component.html",
  styleUrls: ["./country-reminders.component.scss"],
  standalone: false,
})
export class CountryRemindersComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  private subscriptions: Subscription[] = [];
  faSpinner = faSpinner;
  dataSource = new MatTableDataSource<Reminder>([]);

  localizationConstants = LocalizationConstants;

  pageSize: number = 10;

  columns = [
    { key: "countryName", title: LocalizationConstants.REMINDER_COUNTRY },
    { key: "companyFullName", title: LocalizationConstants.REMINDER_COMPANY },
    { key: "type", title: LocalizationConstants.REMINDER_TYPE },
    { key: "interval", title: LocalizationConstants.REMINDER_INTERVAL },
    { key: "daysBefore", title: LocalizationConstants.REMINDER_DAYS_BEFORE },
    { key: "daysAfter", title: LocalizationConstants.REMINDER_DAYS_AFTER },
    { key: "to", title: LocalizationConstants.REMINDER_TO },
    { key: "cc", title: LocalizationConstants.REMINDER_CC },
    { key: "bcc", title: LocalizationConstants.REMINDER_BCC },
    { key: "nextRemind", title: LocalizationConstants.REMINDER_NEXT_REMIND },
  ];

  columnKeysAll = [...this.columns.map((x) => x.key), "action1", "action2"];

  constructor(
    private reminderService: ReminderService,
    private modalService: ModalService,
    private globalOverlayService: GlobalOverlayService,
  ) {}

  ngOnInit(): void {
    this.getReminders();
  }

  ngAfterViewInit() {
    this.dataSource.filterPredicate = this.quickSearchFilter;
    this.dataSource.sort = this.sort;
    this.dataSource.paginator = this.paginator;
  }

  getReminders() {
    this.globalOverlayService.show();
    this.reminderService
      .getByFamcode()
      .pipe(finalize(() => this.globalOverlayService.close()))
      .subscribe((response) => {
        this.dataSource.data = response;
      });
  }

  onEditReminder(reminder: Reminder) {
    const subscription = this.modalService
      .openDefaultModal(ReminderModalComponent, reminder)
      .subscribe((result) => {
        // modal just closed without saving
        if (!result) {
          return;
        }

        const existingReminderIndex = this.dataSource.data.findIndex((x) => x.id == result.id);

        if (existingReminderIndex !== -1) {
          this.dataSource.data[existingReminderIndex] = result;
          this.dataSource.data = [...this.dataSource.data];
        }
      });

    this.subscriptions.push(subscription);
  }

  onRemoveReminder(reminder: Reminder) {
    const resultObservable = this.modalService.openConfirmationModal(
      new ConfirmationDialogData(LocalizationConstants.COMPANY_REMINDER_REMOVE_DIALOG_TITLE, [
        LocalizationConstants.COMPANY_REMINDER_REMOVE_DIALOG_CONTENT,
      ]),
    );

    const subscription = resultObservable.subscribe((result) => {
      if (result) {
        this.removeReminder(reminder);
      }
    });

    this.subscriptions.push(subscription);
  }

  private removeReminder(reminder: Reminder) {
    reminder.pending = true;
    this.reminderService
      .delete(reminder.id)
      .pipe(finalize(() => (reminder.pending = false)))
      .subscribe(
        () => (this.dataSource.data = this.dataSource.data.filter((r) => r.id != reminder.id)),
      );
  }

  public quickSearchFilter(reminder: Reminder, searchValue?: string): boolean {
    if (!searchValue) return true;
    const search = searchValue.toLowerCase();
    return (
      reminder.companyFullName?.toLowerCase().includes(search) ||
      reminder.countryName?.toLowerCase().includes(search) ||
      reminder.bcc?.toLowerCase().includes(search) ||
      reminder.cc?.toLowerCase().includes(search) ||
      reminder.to?.toLowerCase().includes(search) ||
      reminder.type === ReminderType[search]
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((x) => x.unsubscribe());
  }
}
